/**
 * Applications List Page
 * Premium view of all user applications with filtering, sorting, and actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Skeleton,
  Alert,
  Tooltip,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Flight as FlightIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  HourglassEmpty as ProcessingIcon,
  Cancel as RejectedIcon,
  Description as DraftIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL, STORAGE_KEYS } from '../../constants';
import { readJsonResponse } from '../../lib/apiBaseUrl';

// Types
interface Application {
  id: string;
  tracking_number: string;
  destination_country: string;
  destination_country_code: string;
  visa_type: string;
  status: string;
  applicant_first_name: string;
  applicant_last_name: string;
  travel_date: string;
  created_at: string;
  updated_at: string;
  government_fee: number;
  service_fee: number;
  total_fee: number;
}

interface Stats {
  total: number;
  draft: number;
  pending: number;
  processing: number;
  approved: number;
  rejected: number;
}

// Status configuration
const STATUS_CONFIG: Record<string, { color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; label: string }> = {
  draft: { color: 'default', label: 'Draft' },
  pending: { color: 'warning', label: 'Pending Review' },
  submitted: { color: 'info', label: 'Submitted' },
  processing: { color: 'primary', label: 'Processing' },
  under_review: { color: 'primary', label: 'Under Review' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

// Flag URL helper
const getFlagUrl = (countryCode: string) => 
  `https://flagcdn.com/w40/${countryCode?.toLowerCase()}.png`;

export default function ApplicationsListPage() {
  const navigate = useNavigate();
  useAuth();
  
  // State
  const [applications, setApplications] = useState<Application[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
      
      const [appsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/applications`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API_BASE_URL}/applications/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (appsRes.ok) {
        const appsData = await readJsonResponse<any>(appsRes);
        const list = Array.isArray(appsData.data)
          ? appsData.data
          : Array.isArray(appsData.applications)
            ? appsData.applications
            : [];
        setApplications(list);
      }

      if (statsRes.ok) {
        const statsData = await readJsonResponse<any>(statsRes);
        // Backend returns { success, data: { counts: {...} } } — unwrap to get counts
        const counts = statsData?.data?.counts || statsData?.counts || statsData;
        setStats({
          total: counts.total || 0,
          draft: counts.draft || 0,
          pending: counts.pending || 0,
          processing: counts.processing || counts.inReview || 0,
          approved: counts.approved || 0,
          rejected: counts.rejected || 0,
        });
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Filter applications
  const filteredApplications = applications.filter((app) => {
    const matchesSearch = 
      app.tracking_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.destination_country?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      `${app.applicant_first_name} ${app.applicant_last_name}`.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Handlers
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, app: Application) => {
    setAnchorEl(event.currentTarget);
    setSelectedApp(app);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedApp(null);
  };

  const handleView = () => {
    if (selectedApp) {
      navigate(`/applications/${selectedApp.id}`);
    }
    handleMenuClose();
  };

  const handleEdit = () => {
    if (selectedApp) {
      navigate(`/applications/${selectedApp.id}`);
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (selectedApp && window.confirm('Are you sure you want to delete this application?')) {
      try {
        const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        await fetch(`${API_BASE_URL}/applications/${selectedApp.id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        fetchApplications();
      } catch (err) {
        console.error('Delete failed:', err);
      }
    }
    handleMenuClose();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Stats Cards Component
  const StatsCards = () => (
    <Grid container spacing={3} sx={{ mb: 4 }}>
      {[
        { label: 'Total', value: stats?.total || 0, color: '#1273E5', icon: <FlightIcon /> },
        { label: 'Draft', value: stats?.draft || 0, color: '#9e9e9e', icon: <DraftIcon /> },
        { label: 'Pending', value: stats?.pending || 0, color: '#ff9800', icon: <PendingIcon /> },
        { label: 'Processing', value: stats?.processing || 0, color: '#2196f3', icon: <ProcessingIcon /> },
        { label: 'Approved', value: stats?.approved || 0, color: '#4caf50', icon: <ApprovedIcon /> },
        { label: 'Rejected', value: stats?.rejected || 0, color: '#f44336', icon: <RejectedIcon /> },
      ].map((stat) => (
        <Grid item xs={6} sm={4} md={2} key={stat.label}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              transition: 'all 0.2s',
              border: statusFilter === stat.label.toLowerCase() ? `2px solid ${stat.color}` : '2px solid transparent',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: 3 },
            }}
            onClick={() => setStatusFilter(stat.label.toLowerCase() === 'total' ? 'all' : stat.label.toLowerCase())}
          >
            <CardContent sx={{ textAlign: 'center', py: 2 }}>
              <Box sx={{ color: stat.color, mb: 1 }}>{stat.icon}</Box>
              <Typography variant="h4" fontWeight="bold" sx={{ color: stat.color }}>
                {loading ? <Skeleton width={40} sx={{ mx: 'auto' }} /> : stat.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stat.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              My Applications
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage your visa applications
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<AddIcon />}
            onClick={() => navigate('/applications/new')}
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 2,
              background: 'linear-gradient(135deg, #1273E5 0%, #0958A5 100%)',
              boxShadow: '0 4px 14px rgba(18, 115, 229, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0958A5 0%, #1273E5 100%)',
              },
            }}
          >
            New Application
          </Button>
        </Box>

        {/* Stats Cards */}
        <StatsCards />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Table Card */}
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {/* Toolbar */}
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0', display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by tracking number, country, or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            
            <Tabs
              value={statusFilter}
              onChange={(_, v) => setStatusFilter(v)}
              sx={{ ml: 2, minHeight: 40 }}
            >
              <Tab label="All" value="all" sx={{ minHeight: 40, py: 0 }} />
              <Tab label="Pending" value="pending" sx={{ minHeight: 40, py: 0 }} />
              <Tab label="Processing" value="processing" sx={{ minHeight: 40, py: 0 }} />
              <Tab label="Approved" value="approved" sx={{ minHeight: 40, py: 0 }} />
            </Tabs>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title="Refresh">
              <IconButton onClick={fetchApplications} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export as CSV">
              <IconButton onClick={() => {
                const csv = ['Destination,Visa Type,Status,Submitted']
                  .concat(applications.map(a => `${a.destination_country},${a.visa_type},${a.status},${a.created_at}`))
                  .join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'applications.csv';
                link.click();
                URL.revokeObjectURL(url);
              }}>
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Loading Bar */}
          {loading && <LinearProgress />}

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tracking #</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Destination</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Visa Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Travel Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fee</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  // Skeleton rows
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(9)].map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filteredApplications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <FlightIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          No applications found
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {searchQuery ? 'Try a different search term' : 'Start your visa journey today'}
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={() => navigate('/applications/new')}
                        >
                          Create Application
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredApplications
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((app) => {
                      const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                      return (
                        <TableRow 
                          key={app.id} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { bgcolor: '#f5f9ff' },
                          }}
                          onClick={() => navigate(`/applications/${app.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight="600" color="primary">
                              {app.tracking_number || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                src={getFlagUrl(app.destination_country_code)}
                                sx={{ width: 24, height: 24 }}
                                variant="rounded"
                              >
                                🌍
                              </Avatar>
                              <Typography variant="body2">
                                {app.destination_country || '-'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {app.visa_type || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {app.applicant_first_name} {app.applicant_last_name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(app.travel_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusConfig.label}
                              color={statusConfig.color}
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="500">
                              {formatCurrency(app.total_fee)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatDate(app.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMenuOpen(e, app);
                              }}
                            >
                              <MoreIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredApplications.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{ sx: { minWidth: 160 } }}
        >
          <MenuItem onClick={handleView}>
            <ViewIcon fontSize="small" sx={{ mr: 1 }} /> View Details
          </MenuItem>
          <MenuItem onClick={handleEdit} disabled={selectedApp?.status !== 'draft'}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        </Menu>
      </Container>
    </Box>
  );
}
